import React, { useEffect, useMemo, useRef, useState } from 'react';
import { todosSlice } from '../store/todos';
import reorder, { reorderBetweenColumns, reorderMultipleBetweenColumns } from '../utils/reorder';
import { getVisibleTasks } from '../utils/taskFilters';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import Column from './Column';
import useBoardPanScroll from '../hooks/useBoardPanScroll';
import useTaskBoardAutoScroll from '../hooks/useTaskBoardAutoScroll';

import type { StatusGroup, TodoDetails, Task } from '../../types';
import type { RootState } from '../store';
import type { DragStart, DropResult } from '@hello-pangea/dnd';

import { useSelector, useDispatch } from 'react-redux';
import { useUpdateStatusMutation } from '../store/tasks';

import { TASK_STATUS } from '../constants';
import { TaskSelectionProvider, useTaskSelection } from './TaskSelectionContext';
import useEventListener from '../hooks/useEventListener';

interface BoardProps {
    groups: StatusGroup[];
    todoId: number;
}

const Board = ({ groups, todoId }: BoardProps) => {
    return (
        <TaskSelectionProvider>
            <BoardInner groups={groups} todoId={todoId} />
        </TaskSelectionProvider>
    );
};

const BoardInner = ({ groups, todoId }: BoardProps) => {
    const dispatch = useDispatch();
    const settings = useSelector((state: RootState) => state.settings);
    const [ordered, setOrdered] = useState<number[]>(groups.map(g => g.status));
    const [updateTaskStatus] = useUpdateStatusMutation();
    const boardScrollRef = useBoardPanScroll<HTMLDivElement>();
    const taskBoardAutoScroll = useTaskBoardAutoScroll(boardScrollRef);
    /** Пока тянем колонку — полностью глушим auto-scroll hello-pangea (в т.ч. вертикальный) */
    const disableAutoScrollRef = useRef(false);
    const { selectedIds, setDraggingId, clearSelected } = useTaskSelection();

    const autoScrollerOptions = useMemo(() => ({
        get disabled() {
            return disableAutoScrollRef.current;
        },
    }), []);

    const clearColumnDragging = () => {
        disableAutoScrollRef.current = false;
        document.documentElement.classList.remove('is-dragging-column');
        boardScrollRef.current?.classList.remove('board-scroll--column-dragging');
    };

    useEffect(() => clearColumnDragging, []);

    useEffect(() => {
        const restoreLockedScroll = (event: Event) => {
            if (!disableAutoScrollRef.current) {
                return;
            }
            const target = event.target;
            if (!(target instanceof HTMLElement)) {
                return;
            }
            if (!target.classList.contains('tasks-list')) {
                return;
            }
            const locked = target.dataset.lockedScrollTop;
            if (locked == null) {
                return;
            }
            const lockedTop = Number(locked);
            if (target.scrollTop !== lockedTop) {
                target.scrollTop = lockedTop;
            }
        };

        // capture: ловим scroll от любых .tasks-list
        document.addEventListener('scroll', restoreLockedScroll, true);
        return () => document.removeEventListener('scroll', restoreLockedScroll, true);
    }, []);

    const onBeforeDragStart = (start: DragStart) => {
        if (start.type !== 'COLUMN') {
            disableAutoScrollRef.current = false;
            taskBoardAutoScroll.start();
            return;
        }

        taskBoardAutoScroll.stop();
        disableAutoScrollRef.current = true;
        document.documentElement.classList.add('is-dragging-column');
        boardScrollRef.current?.classList.add('board-scroll--column-dragging');

        document.querySelectorAll<HTMLElement>('.tasks-list').forEach((list) => {
            list.dataset.lockedScrollTop = String(list.scrollTop);
        });
    };

    const onDragStart = (start: DragStart) => {
        if (start.type === 'COLUMN') {
            disableAutoScrollRef.current = true;
            return;
        }
        if (start.type === 'TASK') {
            setDraggingId(Number(start.draggableId));
        }
    };

    const onDragEnd = (result: DropResult) => {
        taskBoardAutoScroll.stop();
        clearColumnDragging();
        setDraggingId(null);
        document.querySelectorAll<HTMLElement>('.tasks-list').forEach((list) => {
            delete list.dataset.lockedScrollTop;
        });

        if (!result.destination) {
            return;
        }

        const source = result.source;
        const destination = result.destination;

        if (source.droppableId === destination.droppableId && source.index === destination.index) {
            return;
        }

        if (result.type === 'COLUMN') {
            const reorderedorder = reorder(ordered, source.index, destination.index);
            setOrdered(reorderedorder);
            return;
        }

        const newStatus = Number(destination.droppableId);
        const taskId = Number(result.draggableId);
        const movingIds = selectedIds.includes(taskId) && selectedIds.length > 1
            ? selectedIds
            : [taskId];

        const visibleOf = (columnTasks: Task[]) => getVisibleTasks(
            columnTasks,
            settings.search,
            settings.filters,
        );

        const data = movingIds.length > 1
            ? reorderMultipleBetweenColumns({
                columns: groups,
                columnOrder: ordered,
                source,
                destination,
                draggedId: taskId,
                selectedIds: movingIds,
                getVisibleTasks: visibleOf,
            })
            : reorderBetweenColumns({
                columns: groups,
                source,
                destination,
                taskId,
                getVisibleTasks: visibleOf,
            });

        movingIds.forEach((id) => {
            const original = groups
                .flatMap((group) => group.tasks)
                .find((item) => Number(item.taskId) === Number(id));
            if (original && Number(original.status) !== newStatus) {
                updateTaskStatus({ ...original, status: newStatus });
            }
        });

        // @ts-ignore
        dispatch(todosSlice.util.updateQueryData('getTodoById', { todoId: todoId }, (draft: TodoDetails) => {
            draft.groups = data.columns;
        }));
        clearSelected();
    };

    useEventListener('keydown', (event) => {
        if (event.key !== 'Escape') {
            return;
        }
        const activeElement = document.activeElement;
        const isInputElement = activeElement && (
            activeElement.tagName === 'INPUT'
                || activeElement.tagName === 'TEXTAREA'
                || activeElement.getAttribute('contenteditable')
        );
        if (isInputElement) {
            return;
        }
        clearSelected();
    });

    const getGroupByStatus = (status: number) => {
        return groups.find((column) => Number(column.status) === Number(status))!;
    };

    return (
        <div className="board-scroll" ref={boardScrollRef}>
            <DragDropContext
                onBeforeDragStart={onBeforeDragStart}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                autoScrollerOptions={autoScrollerOptions}
            >
                <Droppable
                    droppableId="board"
                    type="COLUMN"
                    direction="horizontal"
                    ignoreContainerClipping={true}
                >
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="board"
                            style={{
                                ['--app-card-width' as string]: `${settings.theme?.cardWidth || 300}px`,
                                ['--app-radius' as string]: `${settings.theme?.borderRadius ?? 8}px`,
                                ['--app-font-size' as string]: `${settings.theme?.fontSize ?? 14}px`,
                                ['--app-card-title-weight' as string]: settings.theme?.regularCardTitles ? '400' : '600',
                            }}
                        >
                            {ordered.map((status, index) => (
                                <Column
                                    key={status}
                                    index={index}
                                    title={TASK_STATUS[status]}
                                    todoId={Number(todoId)}
                                    tasks={getGroupByStatus(status).tasks}
                                    columnId={getGroupByStatus(status).status}
                                />
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
};

export default Board;
